/**
 * Scope as a map (#1665): each track a lane across Must, If time and the next
 * cycle. An epic is the band its parts sit under, so the broadest work is the
 * largest shape; priority is the one saturated colour. Hover or focus shows
 * what an item belongs to, what blocks it and what it blocks. A card moves by
 * dragging it, or from its panel, which is also where its comments are.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import {
  Badge,
  Button,
  Caption1,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  OverlayDrawer,
  Spinner,
  Switch,
  ToggleButton,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import {
  ArrowRight12Regular,
  CalendarLtr16Regular,
  Comment16Regular,
  Dismiss24Regular,
  Person16Regular,
  QuestionCircle16Regular,
  Warning16Regular,
} from '@fluentui/react-icons'
import { useIssueComments } from '../../hooks/usePlanning'
import { COL_KEYS, cardId, colName, colOf, isHouse, whoOf } from '../../lib/planning'
import type {
  ColKey,
  ItemLevel,
  Plan,
  PlanEdit,
  PlanScopeItem,
  PlanState,
  Priority,
} from '../../types/planning'
import { IssueNote } from './IssueNote'
import { CommitInput, IssueLink, PlanChip } from './PlanParts'

const PRIORITIES: Priority[] = ['P0', 'P1', 'P2', 'P3']
const LEVELS: ItemLevel[] = ['epic', 'feature', 'ticket', 'question', 'task']
const LEVEL_NAME: Record<ItemLevel, string> = {
  epic: 'Epic',
  feature: 'Feature',
  ticket: 'Ticket',
  question: 'Decision',
  task: 'Task',
}
// The fill behind a priority's label, and the edge of a card that has it.
const PRI_FILL: Record<Priority, string> = {
  P0: tokens.colorPaletteRedBackground3,
  P1: tokens.colorPaletteDarkOrangeBackground3,
  P2: tokens.colorBrandBackground,
  P3: tokens.colorNeutralBackground5,
}
const NO_TRACK = 'Other'
type Col = ColKey | 'along'

const useStyles = makeStyles({
  bar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    marginBottom: tokens.spacingVerticalS,
  },
  group: { display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px' },
  groupName: { color: tokens.colorNeutralForeground3, marginRight: '2px' },
  map: {
    position: 'relative',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    background: tokens.colorNeutralBackground1,
    overflow: 'hidden',
  },
  grid: { display: 'grid', gridTemplateColumns: 'minmax(120px, 160px) repeat(var(--cols), minmax(0, 1fr))' },
  head: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    background: tokens.colorNeutralBackground2,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    minWidth: 0,
  },
  headName: { fontWeight: tokens.fontWeightSemibold },
  headDays: { display: 'block', color: tokens.colorNeutralForeground3 },
  subHead: { display: 'flex', alignItems: 'center', gap: '6px', paddingTop: '4px', paddingBottom: '4px' },
  // The cycle's days as a strip, with today and the dated markers on it.
  strip: {
    position: 'relative',
    height: '18px',
    marginTop: '4px',
    borderRadius: '2px',
    background: tokens.colorNeutralBackground4,
  },
  today: {
    position: 'absolute',
    top: '-3px',
    bottom: '-3px',
    width: '2px',
    background: tokens.colorBrandForeground1,
  },
  marker: {
    position: 'absolute',
    top: '4px',
    width: '10px',
    height: '10px',
    transform: 'translateX(-50%) rotate(45deg)',
    border: `2px solid ${tokens.colorNeutralBackground1}`,
    padding: 0,
    cursor: 'default',
  },
  lane: { display: 'contents' },
  laneName: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: tokens.spacingHorizontalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    minWidth: 0,
  },
  laneTitle: { fontWeight: tokens.fontWeightSemibold, overflowWrap: 'anywhere' },
  // Its parts' columns: the band spans them.
  band: {
    gridRow: 'auto',
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto',
    alignItems: 'center',
    gap: `2px ${tokens.spacingHorizontalM}`,
    margin: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS} 0`,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderLeft: '6px solid var(--pri)',
    borderRadius: tokens.borderRadiusMedium,
    background: `linear-gradient(90deg, color-mix(in srgb, var(--pri) 12%, ${tokens.colorNeutralBackground1}), ${tokens.colorNeutralBackground1} 75%)`,
    textAlign: 'left',
    cursor: 'pointer',
    color: tokens.colorNeutralForeground1,
    font: 'inherit',
  },
  bandTitle: { fontSize: tokens.fontSizeBase500, fontWeight: tokens.fontWeightBold, lineHeight: tokens.lineHeightBase500 },
  bar2: { gridColumn: '2', height: '6px', borderRadius: '3px', background: tokens.colorNeutralBackground4, overflow: 'hidden' },
  barFill: { display: 'block', height: '100%', background: 'var(--pri)' },
  cell: {
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'flex-start',
    gap: '6px',
    padding: tokens.spacingHorizontalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    minHeight: '56px',
    minWidth: 0,
  },
  cellBanded: { borderTop: 'none' },
  over: { background: tokens.colorBrandBackground2, outline: `2px dashed ${tokens.colorBrandStroke1}`, outlineOffset: '-2px' },
  card: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 0,
    padding: '6px 8px 6px 10px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderLeft: '4px solid var(--pri)',
    borderRadius: tokens.borderRadiusMedium,
    background: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    textAlign: 'left',
    font: 'inherit',
    cursor: 'grab',
    touchAction: 'none',
    transitionProperty: 'opacity, box-shadow',
    transitionDuration: tokens.durationFast,
    ':hover': { boxShadow: tokens.shadow4 },
  },
  feature: { flex: '1 1 200px' },
  ticket: {
    flex: '1 1 200px',
    background: `color-mix(in srgb, ${tokens.colorPaletteDarkOrangeBackground3} 7%, ${tokens.colorNeutralBackground1})`,
  },
  question: { flex: '1 1 200px', borderTopStyle: 'dashed', borderRightStyle: 'dashed', borderBottomStyle: 'dashed' },
  task: { flex: '0 1 auto', flexDirection: 'row', alignItems: 'center', padding: '3px 8px', fontSize: tokens.fontSizeBase200 },
  fixed: { cursor: 'pointer', touchAction: 'auto' },
  dragging: { zIndex: 3, boxShadow: tokens.shadow16 },
  title: { fontWeight: tokens.fontWeightSemibold, overflowWrap: 'anywhere' },
  meta: { display: 'flex', flexWrap: 'wrap', gap: '4px 6px', alignItems: 'center', fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  pri: {
    display: 'inline-block',
    minWidth: '22px',
    padding: '0 4px',
    borderRadius: '2px',
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightBold,
    lineHeight: '16px',
    textAlign: 'center',
    background: 'var(--pri)',
    color: tokens.colorNeutralForegroundOnBrand,
  },
  priLow: { color: tokens.colorNeutralForeground1 },
  flag: { display: 'inline-flex', alignItems: 'center', gap: '3px' },
  blocked: { color: tokens.colorPaletteRedForeground1, fontWeight: tokens.fontWeightSemibold },
  waiting: { color: tokens.colorPaletteDarkOrangeForeground1, fontWeight: tokens.fontWeightSemibold },
  done: { opacity: 0.6 },
  // Focus on one item: it and its relations stay, the rest steps back.
  dim: { opacity: 0.22 },
  lit: { boxShadow: `0 0 0 2px ${tokens.colorBrandStroke1}` },
  litBlocker: { boxShadow: `0 0 0 2px ${tokens.colorPaletteRedBorderActive}` },
  selected: { boxShadow: `0 0 0 2px ${tokens.colorStrokeFocus2}` },
  links: { position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' },
  linkFamily: { fill: 'none', stroke: tokens.colorBrandForeground1, strokeWidth: 2, opacity: 0.75 },
  linkBlock: { fill: 'none', stroke: tokens.colorPaletteRedForeground1, strokeWidth: 2, strokeDasharray: '5 4' },
  linkQuiet: { fill: 'none', stroke: tokens.colorPaletteRedForeground1, strokeWidth: 1.5, strokeDasharray: '4 5', opacity: 0.4 },
  empty: { padding: tokens.spacingVerticalL, textAlign: 'center', color: tokens.colorNeutralForeground3 },
  // Detail panel
  facts: { display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' },
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, marginTop: tokens.spacingVerticalM },
  sectionName: { fontWeight: tokens.fontWeightSemibold },
  moves: { display: 'flex', flexWrap: 'wrap', gap: '4px' },
  rel: { justifyContent: 'flex-start', textAlign: 'left' },
  comment: {
    padding: tokens.spacingHorizontalS,
    background: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },
})

type Rel = 'parent' | 'part' | 'blocker' | 'blocks'

const priOf = (c: PlanScopeItem): Priority => c.pri ?? 'P3'
const levelOf = (c: PlanScopeItem): ItemLevel => c.level ?? (isHouse(c) ? 'task' : 'feature')
const rank = (c: PlanScopeItem) =>
  PRIORITIES.indexOf(priOf(c)) * 10 + LEVELS.indexOf(levelOf(c)) + (c.pri ? 0 : 5)
const fmt = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })
/** Markers grouped by day, in date order. */
function sameDay<M extends { date: string }>(markers: M[]): [string, M[]][] {
  const days = new Map<string, M[]>()
  markers.forEach((m) => days.set(m.date, [...(days.get(m.date) ?? []), m]))
  return [...days.entries()].sort(([a], [b]) => a.localeCompare(b))
}
const priStyle = (c: PlanScopeItem) => ({ '--pri': PRI_FILL[priOf(c)] }) as CSSProperties

export function PlanMap({
  plan,
  state,
  edit,
  cycle = plan.cycle,
}: {
  plan: Plan
  state: PlanState
  edit: (e: PlanEdit) => void
  cycle?: string
}) {
  const styles = useStyles()
  const scope = useMemo(() => plan.scope ?? [], [plan.scope])
  const bands = useMemo(
    () => [...scope.filter((c) => levelOf(c) === 'epic'), ...(plan.parents ?? [])],
    [scope, plan.parents],
  )
  const all = useMemo(() => [...scope, ...(plan.parents ?? [])], [scope, plan.parents])
  const byNumber = useMemo(() => {
    const m = new Map<number, PlanScopeItem>()
    all.forEach((c) => c.refs.forEach((n) => m.set(n, c)))
    return m
  }, [all])

  const [pris, setPris] = useState<Set<Priority>>(new Set(PRIORITIES))
  const [levels, setLevels] = useState<Set<ItemLevel>>(new Set(LEVELS))
  const [blockedOnly, setBlockedOnly] = useState(false)
  const [deps, setDeps] = useState(true)
  const [hover, setHover] = useState<string | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  const ids = useMemo(() => new Set(all.map(cardId)), [all])
  // A refresh can take away the item that is open or hovered: then nothing is focused.
  const focus = (open && ids.has(open) ? open : null) ?? (hover && ids.has(hover) ? hover : null)
  useEffect(() => {
    if (open && !ids.has(open)) setOpen(null)
    if (hover && !ids.has(hover)) setHover(null)
  }, [ids, open, hover])

  const shown = (c: PlanScopeItem) =>
    pris.has(priOf(c)) && levels.has(levelOf(c)) && (!blockedOnly || !!c.blocked_by?.length)

  // Who relates to whom, both ways, by card id.
  const relations = useMemo(() => {
    const out = new Map<string, [string, Rel][]>()
    const add = (a: string, b: string, r: Rel) => out.set(a, [...(out.get(a) ?? []), [b, r]])
    all.forEach((c) => {
      const id = cardId(c)
      const parent = c.parent ? byNumber.get(c.parent) : undefined
      if (parent) {
        add(id, cardId(parent), 'parent')
        add(cardId(parent), id, 'part')
      }
      ;(c.blocked_by ?? []).forEach((n) => {
        const b = byNumber.get(n)
        if (!b) return
        add(id, cardId(b), 'blocker')
        add(cardId(b), id, 'blocks')
      })
    })
    return out
  }, [all, byNumber])

  const cols: Col[] = useMemo(
    () => [...COL_KEYS, ...(scope.some((c) => c.col === 'along') ? (['along'] as Col[]) : [])],
    [scope],
  )
  const where = (c: PlanScopeItem): Col => {
    if (c.col === 'along') return 'along'
    const k = colOf(c, state) as ColKey
    return COL_KEYS.includes(k) ? k : 'maybe'
  }
  // A band spans the columns its parts are in this cycle, or its own.
  const spanOf = (b: PlanScopeItem): [number, number] => {
    const at = (relations.get(cardId(b)) ?? [])
      .filter(([, r]) => r === 'part')
      .map(([id]) => all.find((c) => cardId(c) === id))
      .filter((c): c is PlanScopeItem => !!c && scope.includes(c))
      .map((c) => cols.indexOf(where(c)))
    if (scope.includes(b)) at.push(cols.indexOf(where(b)))
    return at.length ? [Math.min(...at), Math.max(...at)] : [0, cols.length - 1]
  }

  // Lanes: a track each, the most urgent first.
  const lanes = useMemo(() => {
    const m = new Map<string, PlanScopeItem[]>()
    all.forEach((c) => {
      const t = c.track || NO_TRACK
      m.set(t, [...(m.get(t) ?? []), c])
    })
    return [...m.entries()].sort(
      ([a, x], [b, y]) =>
        (a === NO_TRACK ? 1 : 0) - (b === NO_TRACK ? 1 : 0) ||
        Math.min(...x.map(rank)) - Math.min(...y.map(rank)) ||
        a.localeCompare(b),
    )
  }, [all])

  // Grid rows: the heads take rows 1 and 2, then each lane its bands and one row of cards.
  const rowStart = useMemo(() => {
    let r = 3
    return lanes.map(([lane]) => {
      const at = r
      r += bands.filter((b) => (b.track || NO_TRACK) === lane && shown(b)).length + 1
      return at
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `shown` reads these three filters
  }, [lanes, bands, pris, levels, blockedOnly])

  const move = (c: PlanScopeItem, to: ColKey) => edit({ op: 'set', path: ['cols', cardId(c)], value: to })
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  // The pointer lifts over the card it dropped: that click is the drop, not an open.
  const dropped = useRef(0)
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    dropped.current = Date.now()
    const to = over?.data.current?.col as ColKey | undefined
    const c = all.find((x) => cardId(x) === String(active.id))
    if (c && to && to !== where(c)) move(c, to)
  }

  // Connectors over the map: the focused item's family and blockers, or, with
  // nothing focused, every blocker so a blocked card shows it without a hover.
  const mapRef = useRef<HTMLDivElement>(null)
  const [paths, setPaths] = useState<{ d: string; cls: string }[]>([])
  const { linkBlock, linkFamily, linkQuiet } = styles
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const el = mapRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => setTick((t) => t + 1))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  useLayoutEffect(() => {
    const host = mapRef.current
    if (!host) return
    const box = host.getBoundingClientRect()
    const node = (id: string) => host.querySelector<HTMLElement>(`[data-card="${id}"]`)
    const curve = (a: HTMLElement, b: HTMLElement) => {
      const ra = a.getBoundingClientRect()
      const rb = b.getBoundingClientRect()
      const right = ra.right < rb.left
      const left = rb.right < ra.left
      const x1 = (right ? ra.right : left ? ra.left : ra.left + ra.width / 2) - box.left
      const x2 = (right ? rb.left : left ? rb.right : rb.left + rb.width / 2) - box.left
      const y1 = ra.top + ra.height / 2 - box.top
      const y2 = rb.top + rb.height / 2 - box.top
      const dx = Math.max(40, Math.abs(x2 - x1) / 2) * (x2 >= x1 ? 1 : -1)
      return `M${x1},${y1} C${x1 + dx},${y1} ${x2 - dx},${y2} ${x2},${y2}`
    }
    const out: { d: string; cls: string }[] = []
    const link = (a: string, b: string, cls: string) => {
      const na = node(a)
      const nb = node(b)
      if (na && nb) out.push({ d: curve(na, nb), cls })
    }
    if (focus) {
      ;(relations.get(focus) ?? []).forEach(([other, r]) =>
        r === 'blocker'
          ? link(other, focus, linkBlock)
          : r === 'blocks'
            ? link(focus, other, linkBlock)
            : link(focus, other, linkFamily),
      )
    } else if (deps) {
      all.forEach((c) =>
        (c.blocked_by ?? []).forEach((n) => {
          const b = byNumber.get(n)
          if (b) link(cardId(b), cardId(c), linkQuiet)
        }),
      )
    }
    setPaths((old) => (JSON.stringify(old) === JSON.stringify(out) ? old : out))
  }, [focus, relations, deps, all, byNumber, state, pris, levels, blockedOnly, tick, linkBlock, linkFamily, linkQuiet])

  const related = (id: string) => new Map(relations.get(focus ?? '') ?? []).get(id)
  const stateCls = (c: PlanScopeItem) => {
    const id = cardId(c)
    if (!focus) return undefined
    if (id === focus) return styles.selected
    const r = related(id)
    if (!r) return styles.dim
    return r === 'blocker' || r === 'blocks' ? styles.litBlocker : styles.lit
  }
  const hoverProps = (id: string) => ({
    onMouseEnter: () => setHover(id),
    onMouseLeave: () => setHover((h) => (h === id ? null : h)),
    onFocus: () => setHover(id),
    onBlur: () => setHover((h) => (h === id ? null : h)),
    onClick: () => {
      if (Date.now() - dropped.current < 300) return
      setOpen((o) => (o === id ? null : id))
    },
  })

  const label = (c: PlanScopeItem) =>
    [
      `${LEVEL_NAME[levelOf(c)]} ${c.refs.map((n) => `#${n}`).join(' ')}`,
      c.pri ?? 'no priority',
      c.t,
      c.blocked_by?.length ? `blocked by ${c.blocked_by.map((n) => `#${n}`).join(', ')}` : '',
      c.ncom ? `${c.ncom} comments` : '',
    ]
      .filter(Boolean)
      .join(', ')

  const flags = (c: PlanScopeItem) => (
    <>
      {c.blocked_by?.length ? (
        <span className={mergeClasses(styles.flag, styles.blocked)}>
          <Warning16Regular aria-hidden="true" />
          Blocked by {c.blocked_by.map((n) => `#${n}`).join(', ')}
        </span>
      ) : null}
      {levelOf(c) === 'ticket' && c.tag?.[0] !== 'Done' && (
        <span className={mergeClasses(styles.flag, styles.waiting)}>
          <Person16Regular aria-hidden="true" />
          User waiting
        </span>
      )}
      {c.due && (
        <span className={styles.flag}>
          <CalendarLtr16Regular aria-hidden="true" />
          {fmt(c.due)}
        </span>
      )}
      {!!c.ncom && (
        <span className={styles.flag}>
          <Comment16Regular aria-hidden="true" />
          {c.ncom}
        </span>
      )}
      <PlanChip chip={c.tag} />
    </>
  )

  const priBadge = (c: PlanScopeItem) => (
    <span className={mergeClasses(styles.pri, priOf(c) === 'P3' && styles.priLow)}>{c.pri ?? '–'}</span>
  )

  const timeline = plan.timeline
  const current = timeline?.bands.find((b) => b.current) ?? timeline?.bands[0]
  const next = timeline?.bands.find((b) => b !== current && current && b.start >= current.end)
  const today = new Date().toISOString().slice(0, 10)
  // An imported band's dates are free text: a bad one shows no strip and no
  // dates rather than taking the page down (Copilot on #1666).
  const valid = (band?: { start: string; end: string }) =>
    !!band && Date.parse(band.end) - Date.parse(band.start) > 0
  const strip = (band?: { start: string; end: string }) => {
    if (!band || !valid(band)) return null
    const span = Date.parse(band.end) - Date.parse(band.start)
    const pct = (d: string) => ((Date.parse(d) - Date.parse(band.start)) / span) * 100
    const inside = (d: string) => d >= band.start && d < band.end
    return (
      <div className={styles.strip} aria-hidden={!timeline?.markers.some((m) => inside(m.date))}>
        {inside(today) && <span className={styles.today} style={{ left: `${pct(today)}%` }} title="Today" />}
        {sameDay((timeline?.markers ?? []).filter((m) => inside(m.date))).map(([date, ms]) => {
          // Two items due the same day would sit on one spot and hide each
          // other: the day gets one marker that names them all (Copilot on #1666).
          const what = ms.map((m) => m.sub || m.title).join(' · ')
          const kind = ms.some((m) => m.kind === 'red') ? 'red' : ms.some((m) => m.kind === 'amber') ? 'amber' : ''
          return (
            <button
              type="button"
              key={date}
              className={styles.marker}
              style={{
                left: `${pct(date)}%`,
                background:
                  kind === 'red'
                    ? tokens.colorPaletteRedBackground3
                    : kind === 'amber'
                      ? tokens.colorPaletteDarkOrangeBackground3
                      : tokens.colorBrandBackground,
              }}
              title={`${fmt(date)} · ${what}`}
              aria-label={`${fmt(date)}: ${what}`}
              data-testid={`map-marker-${date}`}
            />
          )
        })}
      </div>
    )
  }
  // Two rows of heads: the cycles in time (this one over Must and If time),
  // then the columns with their counts.
  const days = (band?: { start: string; end: string }) =>
    band && valid(band)
      ? `${fmt(band.start)} – ${fmt(new Date(Date.parse(band.end) - 864e5).toISOString().slice(0, 10))}`
      : ''
  const timeHead = (label: string, band: { start: string; end: string } | undefined, from: number, to: number) => (
    <div className={styles.head} style={{ gridColumn: `${from + 2} / ${to + 3}`, gridRow: 1 }}>
      <span className={styles.headName}>{label}</span>
      <Caption1 className={styles.headDays}>{days(band)}</Caption1>
      {strip(band)}
    </div>
  )
  const count = (k: Col) =>
    scope.filter((c) => levelOf(c) !== 'epic' && where(c) === k && shown(c)).length

  const toggle = <T,>(set: Set<T>, v: T, put: (s: Set<T>) => void) => {
    const s = new Set(set)
    if (s.has(v)) s.delete(v)
    else s.add(v)
    put(s)
  }

  const openCard = open ? all.find((c) => cardId(c) === open) : undefined

  if (all.length === 0) return <Caption1 className={styles.empty}>Nothing on the board for this cycle yet.</Caption1>

  return (
    <>
      <div className={styles.bar} role="toolbar" aria-label="Filter the map">
        <span className={styles.group} role="group" aria-label="Priority">
          <Caption1 className={styles.groupName}>Priority</Caption1>
          {PRIORITIES.map((p) => (
            <ToggleButton key={p} size="small" checked={pris.has(p)} onClick={() => toggle(pris, p, setPris)}>
              {p}
            </ToggleButton>
          ))}
        </span>
        <span className={styles.group} role="group" aria-label="Level">
          <Caption1 className={styles.groupName}>Level</Caption1>
          {LEVELS.map((l) => (
            <ToggleButton key={l} size="small" checked={levels.has(l)} onClick={() => toggle(levels, l, setLevels)}>
              {LEVEL_NAME[l]}
            </ToggleButton>
          ))}
        </span>
        <Switch label="Blocked only" checked={blockedOnly} onChange={(_, d) => setBlockedOnly(d.checked)} />
        <Switch label="Dependencies" checked={deps} onChange={(_, d) => setDeps(d.checked)} />
      </div>

      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className={styles.map} ref={mapRef} data-testid="plan-map">
          <div className={styles.grid} style={{ '--cols': cols.length } as CSSProperties}>
            <div className={styles.head} style={{ gridColumn: 1, gridRow: '1 / span 2' }}>
              <span className={styles.headName}>Track</span>
            </div>
            {timeHead(current?.label ? `This cycle · ${current.label}` : 'This cycle', current, 0, 1)}
            {timeHead(colName(plan, 'later'), next, 2, 2)}
            {cols.includes('along') && (
              <div className={styles.head} style={{ gridColumn: 5, gridRow: '1 / span 2' }}>
                <span className={styles.headName}>{plan.along_title || 'Alongside'}</span>
              </div>
            )}
            {COL_KEYS.map((k, i) => (
              <div
                key={k}
                className={mergeClasses(styles.head, styles.subHead)}
                style={{ gridColumn: i + 2, gridRow: 2 }}
                data-testid={`map-head-${k}`}
              >
                <span className={styles.headName}>{k === 'later' ? 'Next cycle' : colName(plan, k)}</span>
                <Badge appearance="tint" shape="rounded" size="small" color={k === 'must' ? 'severe' : k === 'maybe' ? 'brand' : 'informative'}>
                  {count(k)}
                </Badge>
              </div>
            ))}
            {lanes.map(([lane, items], li) => {
              const laneBands = bands.filter((b) => (b.track || NO_TRACK) === lane && shown(b))
              const open_ = items.filter((c) => c.tag?.[0] !== 'Done').length
              const rows = laneBands.length
              const top = rowStart[li] ?? 2
              return (
                <section key={lane} className={styles.lane} aria-label={lane} data-testid={`map-lane-${lane}`}>
                  <div className={styles.laneName} style={{ gridColumn: 1, gridRow: `${top} / span ${rows + 1}` }}>
                    <span className={styles.laneTitle}>{lane}</span>
                    <Caption1>{open_} open</Caption1>
                  </div>
                  {laneBands.map((b, bi) => {
                    const [s, e] = spanOf(b)
                    const id = cardId(b)
                    const [done, total] = b.progress ?? [0, 0]
                    return (
                      <button
                        type="button"
                        key={id}
                        {...hoverProps(id)}
                        data-card={id}
                        data-testid={`map-band-${id}`}
                        aria-label={`${label(b)}${total ? `, ${done} of ${total} parts done` : ''}`}
                        aria-expanded={open === id}
                        style={{ ...priStyle(b), gridColumn: `${s + 2} / ${e + 3}`, gridRow: top + bi }}
                        className={mergeClasses(styles.band, stateCls(b))}
                      >
                        {priBadge(b)}
                        <span className={styles.bandTitle}>{b.t}</span>
                        <span className={styles.meta}>
                          {b.refs.map((n) => `#${n}`).join(' ')}
                          {total ? ` · ${done}/${total} done` : ''}
                        </span>
                        {total > 0 && (
                          <span className={styles.bar2} aria-hidden="true">
                            <span className={styles.barFill} style={{ width: `${(done / total) * 100}%` }} />
                          </span>
                        )}
                        <span className={styles.meta} style={{ gridColumn: '2 / 4' }}>
                          {flags(b)}
                        </span>
                      </button>
                    )
                  })}
                  {cols.map((k, i) => (
                    <DropCell
                      key={k}
                      lane={lane}
                      col={k}
                      style={{ gridColumn: i + 2, gridRow: top + rows }}
                      className={mergeClasses(styles.cell, rows > 0 && styles.cellBanded)}
                      overClass={styles.over}
                    >
                        {items
                          .filter((c) => scope.includes(c) && levelOf(c) !== 'epic' && where(c) === k && shown(c))
                          .sort((a, b) => rank(a) - rank(b))
                          .map((c) => {
                            const id = cardId(c)
                            const lvl = levelOf(c)
                            const fixed = isHouse(c) || c.col === 'along'
                            return (
                              <DragCard
                                key={id}
                                id={id}
                                fixed={fixed}
                                label={label(c)}
                                expanded={open === id}
                                style={priStyle(c)}
                                handlers={hoverProps(id)}
                                className={mergeClasses(
                                  styles.card,
                                  styles[lvl === 'epic' ? 'feature' : lvl],
                                  fixed && styles.fixed,
                                  c.tag?.[0] === 'Done' && styles.done,
                                  stateCls(c),
                                )}
                                dragClass={styles.dragging}
                              >
                                {lvl === 'task' ? (
                                  <>
                                    {priBadge(c)}
                                    <span>{c.t}</span>
                                    {flags(c)}
                                  </>
                                ) : (
                                  <>
                                    <span className={styles.meta}>
                                      {priBadge(c)}
                                      {lvl === 'question' && <QuestionCircle16Regular aria-hidden="true" />}
                                      <span>
                                        {LEVEL_NAME[lvl]} {c.refs.map((n) => `#${n}`).join(' ')}
                                      </span>
                                      {c.parent && !byNumber.has(c.parent) && <span>in #{c.parent}</span>}
                                    </span>
                                    <span className={styles.title}>{c.t}</span>
                                    <span className={styles.meta}>{flags(c)}</span>
                                  </>
                                )}
                              </DragCard>
                            )
                          })}
                    </DropCell>
                  ))}
                </section>
              )
            })}
          </div>
          <svg className={styles.links} aria-hidden="true" data-testid="map-links">
            {paths.map((p, i) => (
              <path key={i} d={p.d} className={p.cls} />
            ))}
          </svg>
        </div>
      </DndContext>

      <OverlayDrawer
        position="end"
        size="medium"
        open={!!openCard}
        onOpenChange={(_, d) => {
          if (!d.open) setOpen(null)
        }}
      >
        {openCard && (
          <Detail
            key={cardId(openCard)}
            c={openCard}
            plan={plan}
            state={state}
            edit={edit}
            cycle={cycle}
            relations={relations.get(cardId(openCard)) ?? []}
            byId={(id) => all.find((x) => cardId(x) === id)}
            onGo={(id) => setOpen(id)}
            onClose={() => setOpen(null)}
            movable={scope.includes(openCard) && !isHouse(openCard) && openCard.col !== 'along'}
            here={where(openCard)}
          />
        )}
      </OverlayDrawer>
    </>
  )
}

/** A card that drags as a whole; a short press opens it. */
function DragCard({
  id,
  fixed,
  label,
  expanded,
  style,
  handlers,
  className,
  dragClass,
  children,
}: {
  id: string
  fixed: boolean
  label: string
  expanded: boolean
  style: CSSProperties
  handlers: Record<string, () => void>
  className: string
  dragClass: string
  children: ReactNode
}) {
  const { setNodeRef, listeners, transform, isDragging } = useDraggable({ id, disabled: fixed })
  return (
    <button
      type="button"
      ref={setNodeRef}
      {...(fixed ? {} : listeners)}
      {...handlers}
      data-card={id}
      data-testid={`map-card-${id}`}
      aria-label={label}
      aria-expanded={expanded}
      style={{ ...style, transform: CSS.Translate.toString(transform) }}
      className={mergeClasses(className, isDragging && dragClass)}
    >
      {children}
    </button>
  )
}

/** One lane's column: a place a card can be dropped. */
function DropCell({
  lane,
  col,
  style,
  className,
  overClass,
  children,
}: {
  lane: string
  col: Col
  style: CSSProperties
  className: string
  overClass: string
  children: ReactNode
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `${lane}|${col}`, data: { col }, disabled: col === 'along' })
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={mergeClasses(className, isOver && overClass)}
      data-testid={`map-cell-${lane}-${col}`}
    >
      {children}
    </div>
  )
}

/** One issue's newest comments and the box that posts to it. */
function IssueThread({
  cycle,
  issue,
  title,
  sum,
  numbered,
}: {
  cycle: string
  issue: number
  title: string
  sum?: string
  numbered: boolean
}) {
  const styles = useStyles()
  const comments = useIssueComments(cycle, issue)
  return (
    <div className={styles.section} data-testid={`map-thread-${issue}`}>
      <span className={styles.sectionName}>{numbered ? `Comments on #${issue}` : 'Comments'}</span>
      {comments.isLoading && <Spinner size="tiny" label="Reading comments" />}
      {comments.isError && <Caption1>Comments could not be read from GitHub just now.</Caption1>}
      {comments.data?.length === 0 && <Caption1>No comments yet.</Caption1>}
      {comments.data?.map((m) => (
        <div key={m.url || m.at} className={styles.comment} data-testid="map-comment">
          <Caption1 className={styles.headDays}>
            {m.author} · {m.at ? fmt(m.at.slice(0, 10)) : ''}
          </Caption1>
          {m.text}
        </div>
      ))}
      <IssueNote cycle={cycle} issue={issue} title={title} sum={sum} numbered={numbered} />
    </div>
  )
}

const REL_NAME: Record<Rel, string> = { parent: 'Part of', part: 'Parts', blocker: 'Blocked by', blocks: 'Blocks' }

function Detail({
  c,
  plan,
  state,
  edit,
  cycle,
  relations,
  byId,
  onGo,
  onClose,
  movable,
  here,
}: {
  c: PlanScopeItem
  plan: Plan
  state: PlanState
  edit: (e: PlanEdit) => void
  cycle: string
  relations: [string, Rel][]
  byId: (id: string) => PlanScopeItem | undefined
  onGo: (id: string) => void
  onClose: () => void
  movable: boolean
  here: Col
}) {
  const styles = useStyles()
  const id = cardId(c)
  return (
    <>
      <DrawerHeader>
        <DrawerHeaderTitle
          action={<Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} onClick={onClose} />}
        >
          {c.t}
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        <div className={styles.facts}>
          {c.refs.map((n) => (
            <IssueLink key={n} n={n} base={plan.repo_url} />
          ))}
          <span className={mergeClasses(styles.pri, priOf(c) === 'P3' && styles.priLow)} style={priStyle(c)}>
            {c.pri ?? '–'}
          </span>
          <Badge appearance="outline">{LEVEL_NAME[levelOf(c)]}</Badge>
          {c.track && <Badge appearance="outline">{c.track}</Badge>}
          {here !== 'along' && <Badge appearance="tint">{colName(plan, here)}</Badge>}
          {c.progress && (
            <Badge appearance="tint" color="success">
              {c.progress[0]} of {c.progress[1]} parts done
            </Badge>
          )}
          {c.due && <Badge appearance="outline">Due {fmt(c.due)}</Badge>}
          <PlanChip chip={c.tag} />
        </div>

        {movable && (
          <div className={styles.section}>
            <span className={styles.sectionName}>Move to</span>
            <div className={styles.moves}>
              {COL_KEYS.filter((k) => k !== here).map((k) => (
                <Button
                  key={k}
                  size="small"
                  icon={<ArrowRight12Regular />}
                  onClick={() => edit({ op: 'set', path: ['cols', id], value: k })}
                >
                  {colName(plan, k)}
                </Button>
              ))}
            </div>
            <CommitInput
              size="small"
              appearance="underline"
              contentBefore={<Person16Regular aria-hidden="true" />}
              aria-label={`Owner of ${c.t}`}
              placeholder="Owner"
              value={whoOf(c, state)}
              onCommit={(v) => edit({ op: 'set', path: ['who', id], value: v })}
            />
          </div>
        )}

        {(['parent', 'part', 'blocker', 'blocks'] as Rel[]).map((r) => {
          const list = relations.filter(([, x]) => x === r)
          // A blocker from another cycle is not on the map; it is still a blocker (Copilot on #1666).
          // So is a parent that is not on the board.
          const offPlan = (ns: number[]) => ns.filter((n) => !list.some(([other]) => byId(other)?.refs.includes(n)))
          const elsewhere =
            r === 'blocker' ? offPlan(c.blocked_by ?? []) : r === 'parent' && c.parent ? offPlan([c.parent]) : []
          if (!list.length && !elsewhere.length) return null
          return (
            <div key={r} className={styles.section}>
              <span className={styles.sectionName}>{REL_NAME[r]}</span>
              {list.map(([other]) => {
                const o = byId(other)
                if (!o) return null
                return (
                  <Button key={other} className={styles.rel} appearance="subtle" size="small" onClick={() => onGo(other)}>
                    <span className={mergeClasses(styles.pri, priOf(o) === 'P3' && styles.priLow)} style={priStyle(o)}>
                      {o.pri ?? '–'}
                    </span>
                    &nbsp;#{o.refs[0]} {o.t}
                  </Button>
                )
              })}
              {elsewhere.map((n) => (
                <span key={n} data-testid={`map-elsewhere-${n}`}>
                  <IssueLink n={n} base={plan.repo_url} /> not in this plan
                </span>
              ))}
            </div>
          )
        })}

        {c.refs.map((n) => (
          <IssueThread
            key={n}
            cycle={cycle}
            issue={n}
            title={c.refs.length > 1 ? `${c.t} (#${n})` : c.t}
            sum={plan.sums?.[String(n)]?.s}
            numbered={c.refs.length > 1}
          />
        ))}
      </DrawerBody>
    </>
  )
}
